// <Tr>English text</Tr> — auto-translates its children to the active
// language. Use this anywhere dynamic backend content shows up
// in English by default and the rest of the UI is already i18n-aware.
//
// Lazy viewport mode (Feb 2026):
//   On long lists (Bible Dictionary's 4k entries, Concordance,
//   Precepts), rendering every <Tr> eagerly used to fan out hundreds
//   of translation requests at once — saturating the backend and
//   starving other API calls. We now wrap each <Tr> in an
//   IntersectionObserver. A <Tr> only requests its translation
//   when it enters (or comes within 200px of) the viewport.
//   Visible content above the fold appears in the user's language
//   in ~1.5 seconds; the rest streams in as they scroll.
//
//   For tiny strings (e.g. UI labels) we eager-translate immediately
//   since they always paint above the fold.
//
// Internally just defers to useCaptionTranslation so we share the
// same per-(lang,text) cache.
//
// Usage:
//   <p><Tr>{post.body}</Tr></p>
//   <h3><Tr>{movie.title}</Tr></h3>
//   <p><Tr eager>{tinyLabel}</Tr></p>   // skip the IO wait
//
// Limitations:
//   • Only handles a single string child. If you nest JSX, only the
//     plain text gets translated.
//   • Returns the original English while translation is in flight so
//     the UI never blanks. Switches in once cached.
import React, { useEffect, useRef, useState } from "react";
import { useAudio } from "../context/AudioContext";
import useCaptionTranslation from "../hooks/useCaptionTranslation";

// Eager threshold — strings <= this length are always immediately
// requested (no IntersectionObserver delay). UI labels, button
// text, kickers, badges, headings, short paragraphs all fit here.
// We raised this from 60 → 240 so the perceived language-switch
// latency drops from "things gradually fade in as I scroll" to
// "everything visible swaps at once". Long body paragraphs
// (devotional bodies, chapter text) still wait for visibility,
// keeping the network footprint sane on 4k-entry pages.
const EAGER_LEN = 240;

// Sync cache lookup — bypasses the IntersectionObserver wait entirely
// when we already have the translation. We re-read localStorage here
// (instead of importing the useCaptionTranslation singleton Map)
// because Tr.jsx renders BEFORE that module's `cache` Map may have
// been touched in some hot-reload scenarios.
const LS_KEY_TR = "hw:i18n:cache:v2";
let _trCache = null;
function _hasCached(text, lang) {
  if (!text || !lang || lang === "en") return false;
  if (_trCache === null) {
    try {
      _trCache = JSON.parse(window.localStorage.getItem(LS_KEY_TR) || "{}");
    } catch (_) {
      _trCache = {};
    }
  }
  return Object.prototype.hasOwnProperty.call(_trCache, `${lang}::${text}`);
}

export default function Tr({ children, as: As = React.Fragment, eager = false, ...rest }) {
  const text = typeof children === "string" ? children : (Array.isArray(children) ? children.filter((c) => typeof c === "string").join("") : "");
  const { language } = useAudio();
  const ref = useRef(null);
  // Tracks whether THIS instance is allowed to fetch yet. Start true
  // for short strings, the eager prop, English, AND for any string
  // we already have in localStorage cache (which is the common path
  // now that the bundle hydrator pre-loads ~150 strings × 18 langs
  // at boot). Cache-hit means we can render the translation on the
  // very first paint with zero network or scroll wait.
  const shouldTranslate =
    !language ||
    language === "en" ||
    eager ||
    (text && text.length <= EAGER_LEN) ||
    _hasCached(text, language);
  const [visible, setVisible] = useState(shouldTranslate);

  useEffect(() => {
    if (shouldTranslate || visible) return;
    if (!ref.current || typeof IntersectionObserver === "undefined") {
      // SSR / older browsers — fall back to eager.
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
          break;
        }
      }
    }, { rootMargin: "200px 0px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [shouldTranslate, visible]);

  const translated = useCaptionTranslation(visible ? text : "", language);
  const out = (visible && translated) || text || children;

  if (As === React.Fragment) {
    // Need a host element to observe — use a transparent span when
    // we're in lazy mode. For eager translations skip the wrapper to
    // keep DOM identical to before.
    if (shouldTranslate) return <>{out}</>;
    return <span ref={ref} data-tr-lazy="1">{out}</span>;
  }
  return <As ref={ref} {...rest}>{out}</As>;
}
